import {
  Room,
  RoomEvent,
  LocalParticipant,
  RemoteParticipant,
  Track,
  ConnectionState,
} from 'livekit-client';
import type {
  RoomService,
  RoomStateListener,
  PublisherStatsListener,
} from '../ports/RoomService';
import type { LiveKitJwt, PublisherStats } from '@shared/types';
import type {
  RoomState,
  RoomInfo,
  Participant,
  JoinRoomParams,
  RoomConnectionState,
} from '@shared/types';
import { DEFAULT_ROOM_STATE } from '@shared/types';
import { parseJwt } from '@/shared/lib/utils';

/**
 * LiveKit-backed implementation of RoomService.
 *
 * All LiveKit-specific types (Room, Participant, Track) are translated into
 * domain types before being emitted to listeners. When the Rust backend is
 * ready, this adapter can be replaced without changing callers.
 */
export class LiveKitRoomService implements RoomService {
  private lkRoom: Room;
  private state: RoomState = { ...DEFAULT_ROOM_STATE };
  private listeners = new Set<RoomStateListener>();
  private publisherStatsListeners = new Set<PublisherStatsListener>();
  private statsTimer: number | null = null;
  private lastBytesSent = 0;
  private lastStatsTimestamp = 0;
  private publishedTrackSids = new Set<string>();
  private connectedRoomName: string | null = null;
  private isTokenRefreshInFlight = false;
  private activeClonedTracks: MediaStreamTrack[] = [];

  constructor() {
    this.lkRoom = new Room({
      adaptiveStream: true,
      dynacast: true,
      publishDefaults: {
        // LiveKit egress (Room Composite Egress) transcodes the room's
        // outbound WebRTC stream to RTMP. The egress service supports
        // H.264 out of the box; some deployments (Twitch, Kick) reject
        // VP8 for the RTMP push. We pick H.264 as the primary codec
        // and let LiveKit transparently regress to VP8 via the backup
        // codec when a subscriber can't decode H.264.
        videoCodec: 'h264',
        backupCodec: true,
        simulcast: true,
      },
    });
    this.bindRoomEvents();
  }

  async join(params: JoinRoomParams, localStream: MediaStream | null): Promise<void> {
    this.connectedRoomName = params.roomName;
    this.isTokenRefreshInFlight = params.isTokenRefresh === true;
    this.setState({ connectionState: 'connecting', error: null });

    try {
      if (this.lkRoom.state !== ConnectionState.Disconnected) {
        await this.lkRoom.disconnect();
      }

      await this.lkRoom.connect(params.livekitUrl, params.token);

      if (params.participantId) {
        try {
          await this.lkRoom.localParticipant.setMetadata(
            JSON.stringify({ backendId: params.participantId }),
          );
        } catch (err) {
          console.warn('Failed to set participant metadata (backendId will be unavailable to peers):', err);
        }
      }

      if (localStream) {
        const claims = parseJwt<LiveKitJwt>(params.token);

        if (claims.video.canPublish && localStream) {
          const rawVideoTrack = localStream.getVideoTracks()[0];
          const rawAudioTrack = localStream.getAudioTracks()[0];

          if (params.isTokenRefresh) {
            this.activeClonedTracks.forEach((t) => t.stop());
            this.activeClonedTracks = [];
          }

          const videoTrack = params.isTokenRefresh && rawVideoTrack ? rawVideoTrack.clone() : rawVideoTrack;
          const audioTrack = params.isTokenRefresh && rawAudioTrack ? rawAudioTrack.clone() : rawAudioTrack;

          if (params.isTokenRefresh) {
            if (videoTrack) this.activeClonedTracks.push(videoTrack);
            if (audioTrack) this.activeClonedTracks.push(audioTrack);
          }

          if (videoTrack) {
            await this.lkRoom.localParticipant.publishTrack(videoTrack, {
              name: 'camera',
              source: Track.Source.Camera,
            });
          }
          if (audioTrack) {
            await this.lkRoom.localParticipant.publishTrack(audioTrack, {
              name: 'microphone',
              source: Track.Source.Microphone,
            });
          }
          this.rememberPublishedTracks();
        }
      }

      this.startStatsCollection();

      this.setState({
        connectionState: 'connected',
        localParticipant: this.mapLocalParticipant(this.lkRoom.localParticipant),
        participants: this.buildParticipantList(),
      });
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'Failed to connect';
      this.setState({ connectionState: 'error', error: message });
      throw cause;
    } finally {
      this.isTokenRefreshInFlight = false;
    }
  }

  async leave(): Promise<void> {
    if (this.lkRoom.state === ConnectionState.Disconnected) {
      return;
    }
    this.stopStatsCollection();
    this.publishedTrackSids.clear();
    this.connectedRoomName = null;
    await this.lkRoom.disconnect();
    this.setState({ ...DEFAULT_ROOM_STATE });
  }

  async setMicEnabled(enabled: boolean): Promise<void> {
    await this.lkRoom.localParticipant.setMicrophoneEnabled(enabled);
    this.refreshLocalParticipant();
  }

  async setCameraEnabled(enabled: boolean): Promise<void> {
    await this.lkRoom.localParticipant.setCameraEnabled(enabled);
    this.refreshLocalParticipant();
  }

  getState(): RoomState {
    return this.state;
  }

  subscribe(listener: RoomStateListener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  // ─── Private ────────────────────────────────────────────────────────────

  private bindRoomEvents(): void {
    this.lkRoom
      .on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
        this.setState({ connectionState: this.mapConnectionState(state) });
      })
      .on(RoomEvent.ParticipantConnected, () => {
        this.setState({ participants: this.buildParticipantList() });
      })
      .on(RoomEvent.ParticipantDisconnected, () => {
        this.setState({ participants: this.buildParticipantList() });
      })
      .on(RoomEvent.ParticipantMetadataChanged, () => {
        this.setState({ participants: this.buildParticipantList() });
      })
      .on(RoomEvent.TrackSubscribed, (_track, _pub, _participant) => {
        this.setState({ participants: this.buildParticipantList() });
      })
      .on(RoomEvent.TrackUnsubscribed, () => {
        this.setState({ participants: this.buildParticipantList() });
      })
      .on(RoomEvent.TrackMuted, () => {
        this.setState({ participants: this.buildParticipantList() });
      })
      .on(RoomEvent.TrackUnmuted, () => {
        this.setState({ participants: this.buildParticipantList() });
      })
      .on(RoomEvent.ActiveSpeakersChanged, () => {
        this.setState({ participants: this.buildParticipantList() });
      })
      .on(RoomEvent.LocalTrackPublished, () => {
        this.refreshLocalParticipant();
      })
      .on(RoomEvent.LocalTrackUnpublished, () => {
        this.refreshLocalParticipant();
      })
      .on(RoomEvent.Disconnected, () => {
        if (this.isTokenRefreshInFlight) {
          return;
        }
        this.setState({ ...DEFAULT_ROOM_STATE });
      })
      .on(RoomEvent.TrackPublished, () => {
        this.setState({ participants: this.buildParticipantList() });
      })
      .on(RoomEvent.TrackUnpublished, () => {
        this.setState({ participants: this.buildParticipantList() });
      });
  }

  private buildParticipantList(): Participant[] {
    const remote = Array.from(this.lkRoom.remoteParticipants.values()).map((p) =>
      this.mapRemoteParticipant(p),
    );
    const local = this.mapLocalParticipant(this.lkRoom.localParticipant);
    return [local, ...remote];
  }

  mapLocalParticipant(p: LocalParticipant): Participant {
    return {
      identity: p.identity,
      name: p.name ?? p.identity,
      isLocal: true,
      isCameraEnabled: p.isCameraEnabled,
      isMicEnabled: p.isMicrophoneEnabled,
      isSpeaking: p.isSpeaking,
      videoTrack: this.extractVideoTrack(p),
      audioTrack: this.extractAudioTrack(p),
      backendId: this.parseBackendId(p.metadata),
    };
  }

  mapRemoteParticipant(p: RemoteParticipant): Participant {
    return {
      identity: p.identity,
      name: p.name ?? p.identity,
      isLocal: false,
      isCameraEnabled: p.isCameraEnabled,
      isMicEnabled: p.isMicrophoneEnabled,
      isSpeaking: p.isSpeaking,
      videoTrack: this.extractVideoTrack(p),
      audioTrack: this.extractAudioTrack(p),
      backendId: this.parseBackendId(p.metadata),
    };
  }

  private parseBackendId(metadata: string | undefined): string | null {
    if (!metadata) return null;
    try {
      const parsed = JSON.parse(metadata) as { backendId?: string };
      return parsed.backendId ?? null;
    } catch {
      return null;
    }
  }

  private extractVideoTrack(
    p: LocalParticipant | RemoteParticipant,
  ): MediaStreamTrack | null {
    const pub = p.getTrackPublication(Track.Source.Camera);
    return pub?.track?.mediaStreamTrack ?? null;
  }

  private extractAudioTrack(
    p: LocalParticipant | RemoteParticipant,
  ): MediaStreamTrack | null {
    const pub = p.getTrackPublication(Track.Source.Microphone);
    return pub?.track?.mediaStreamTrack ?? null;
  }

  private mapConnectionState(state: ConnectionState): RoomConnectionState {
    const map: Record<ConnectionState, RoomConnectionState> = {
      [ConnectionState.Disconnected]: 'disconnected',
      [ConnectionState.Connecting]: 'connecting',
      [ConnectionState.Connected]: 'connected',
      [ConnectionState.Reconnecting]: 'reconnecting',
      [ConnectionState.SignalReconnecting]: 'reconnecting',
    };
    return map[state] ?? 'disconnected';
  }

  private refreshLocalParticipant(): void {
    this.setState({
      localParticipant: this.mapLocalParticipant(this.lkRoom.localParticipant),
      participants: this.buildParticipantList(),
    });
  }

  private setState(patch: Partial<RoomState>): void {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l(this.state));
  }

  // ─── Publisher surface (Phase 1) ────────────────────────────────────────

  isConnected(): boolean {
    return this.lkRoom.state === ConnectionState.Connected;
  }

  getRoomName(): string | null {
    return this.connectedRoomName;
  }

  setRoomInfo(info: RoomInfo): void {
    this.setState({ room: info });
  }

  async publishTracks(localStream: MediaStream): Promise<void> {
    if (!this.isConnected()) {
      return;
    }

    const videoTrack = localStream.getVideoTracks()[0];
    const audioTrack = localStream.getAudioTracks()[0];

    if (videoTrack) {
      const existing = this.lkRoom.localParticipant.getTrackPublication(Track.Source.Camera);
      const sameMediaStream = existing?.track?.mediaStreamTrack === videoTrack;
      let existingCodec: string | undefined = existing?.options?.videoCodec;

      if (existing && !existingCodec && existing.track) {
        existingCodec = await this.getActiveVideoCodec(existing.track);
        if (!existingCodec) {
          // If we couldn't get it from stats, check the room's default setting
          existingCodec = (this.lkRoom as unknown as { options?: { publishDefaults?: { videoCodec?: string } } }).options?.publishDefaults?.videoCodec;
        }
      }

      // Egress can only transcode H.264 to RTMP cleanly. If the existing
      // publication is the same MediaStreamTrack but on a different codec
      // (e.g. VP8 from a prior conference join), unpublish it first so
      // the new publish below comes up on H.264.
      if (existing && (!sameMediaStream || existingCodec !== 'h264')) {
        await this.lkRoom.localParticipant.unpublishTrack(existing.track!);
      }
      if (!existing || !sameMediaStream || existingCodec !== 'h264') {
        await this.lkRoom.localParticipant.publishTrack(videoTrack, {
          name: 'camera',
          source: Track.Source.Camera,
          videoCodec: 'h264',
        });
      }
    }

    if (audioTrack) {
      const existing = this.lkRoom.localParticipant.getTrackPublication(Track.Source.Microphone);
      if (!existing || existing.track?.mediaStreamTrack !== audioTrack) {
        if (existing) {
          await this.lkRoom.localParticipant.unpublishTrack(existing.track!);
        }
        await this.lkRoom.localParticipant.publishTrack(audioTrack, {
          name: 'microphone',
          source: Track.Source.Microphone,
        });
      }
    }

    this.rememberPublishedTracks();
    this.startStatsCollection();
  }

  private async getActiveVideoCodec(track: { getRTCStatsReport(): Promise<RTCStatsReport | undefined> }): Promise<string | undefined> {
    try {
      const report = await track.getRTCStatsReport();
      if (!report) return undefined;

      let codecId: string | undefined;
      for (const value of report.values()) {
        if (value.type === 'outbound-rtp' && !value.isRemote) {
          codecId = value.codecId;
          break;
        }
      }
      if (codecId) {
        const codecStats = report.get(codecId);
        if (codecStats && typeof codecStats.mimeType === 'string') {
          const parts = codecStats.mimeType.split('/');
          return parts[parts.length - 1].toLowerCase();
        }
      }
    } catch (err) {
      console.warn('Failed to retrieve RTC stats for codec detection:', err);
    }
    return undefined;
  }

  async unpublishAllTracks(): Promise<void> {
    this.stopStatsCollection();
    this.publishedTrackSids.clear();
    const publications = Array.from(
      this.lkRoom.localParticipant.trackPublications.values(),
    );
    for (const publication of publications) {
      if (publication.track) {
        await this.lkRoom.localParticipant.unpublishTrack(publication.track);
      }
    }
    this.emitPublisherStats(this.emptyStats());
  }

  onPublisherStatsChanged(listener: PublisherStatsListener): () => void {
    this.publisherStatsListeners.add(listener);
    listener(this.emptyStats());
    return () => {
      this.publisherStatsListeners.delete(listener);
    };
  }

  private rememberPublishedTracks(): void {
    this.publishedTrackSids.clear();
    for (const publication of this.lkRoom.localParticipant.trackPublications.values()) {
      if (publication.trackSid) {
        this.publishedTrackSids.add(publication.trackSid);
      }
    }
  }

  private startStatsCollection(): void {
    if (this.statsTimer !== null) {
      return;
    }
    this.lastBytesSent = 0;
    this.lastStatsTimestamp = 0;
    this.statsTimer = window.setInterval(() => {
      void this.collectPublisherStats();
    }, 1000);
  }

  private stopStatsCollection(): void {
    if (this.statsTimer !== null) {
      window.clearInterval(this.statsTimer);
      this.statsTimer = null;
    }
    this.lastBytesSent = 0;
    this.lastStatsTimestamp = 0;
  }

  private async collectPublisherStats(): Promise<void> {
    if (!this.isConnected() || this.publishedTrackSids.size === 0) {
      this.emitPublisherStats(this.emptyStats());
      return;
    }

    try {
      const publications = Array.from(
        this.lkRoom.localParticipant.trackPublications.values(),
      );
      const aggregate: PublisherStats = this.emptyStats();
      let haveOutbound = false;

      for (const publication of publications) {
        if (!publication.trackSid || !this.publishedTrackSids.has(publication.trackSid)) {
          continue;
        }
        const report = await publication.track?.getRTCStatsReport();
        if (!report) {
          continue;
        }
        report.forEach((value) => {
          if (value.type === 'outbound-rtp' && !value.isRemote) {
            const outbound = value as RTCOutboundRtpStreamStats;
            haveOutbound = true;
            aggregate.packetsSent = (aggregate.packetsSent ?? 0) + (outbound.packetsSent ?? 0);
            if (typeof outbound.framesPerSecond === 'number') {
              aggregate.fps = Math.max(aggregate.fps, Math.round(outbound.framesPerSecond));
            }
            if (typeof outbound.bytesSent === 'number') {
              if (this.lastStatsTimestamp !== 0) {
                const deltaBytes = outbound.bytesSent - this.lastBytesSent;
                const deltaSeconds = (outbound.timestamp - this.lastStatsTimestamp) / 1000;
                if (deltaSeconds > 0) {
                  aggregate.bitrateKbps = Math.max(
                    aggregate.bitrateKbps,
                    Math.round((deltaBytes * 8) / deltaSeconds / 1000),
                  );
                }
              }
              this.lastBytesSent = outbound.bytesSent;
              this.lastStatsTimestamp = outbound.timestamp;
            }
          }
          if (value.type === 'candidate-pair') {
            const pair = value as RTCIceCandidatePairStats;
            if (pair.state === 'succeeded' && typeof pair.currentRoundTripTime === 'number') {
              aggregate.latencyMs = Math.max(
                aggregate.latencyMs,
                Math.round(pair.currentRoundTripTime * 1000),
              );
            }
          }
          if (value.type === 'codec') {
            const codec = value as RTCStats & { mimeType?: string };
            if (codec.mimeType && !aggregate.codec) {
              aggregate.codec = codec.mimeType;
            }
          }
        });
      }

      if (!haveOutbound) {
        aggregate.bitrateKbps = 0;
        aggregate.fps = 0;
        aggregate.latencyMs = 0;
        aggregate.packetsSent = 0;
      }

      this.emitPublisherStats(aggregate);
    } catch {
      // Swallow — LiveKit stats are best-effort.
    }
  }

  private emitPublisherStats(stats: PublisherStats): void {
    for (const listener of this.publisherStatsListeners) {
      listener(stats);
    }
  }

  private emptyStats(): PublisherStats {
    return {
      bitrateKbps: 0,
      fps: 0,
      latencyMs: 0,
      codec: null,
      packetsSent: 0,
      packetsReceived: 0,
    };
  }
}
